/**
 * 휴일(빨간날) 관리 — 자율학습 설정 탭 안에서 사용
 *
 * 일요일과 공휴일은 "하루 전체가 자율학습"이므로 시스템이 휴일을 알아야 한다.
 * 전국 공휴일은 자동으로 채워지고, 매장은 여기서 추가하거나 정상 운영으로 되돌린다.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { PlusIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';
import type { HolidayItem } from '../models';

const apiCall = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
  const token = localStorage.getItem('accessToken') || 'demo-token';
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: '알 수 없는 오류' }));
    throw new Error(errorData.detail || `HTTP ${response.status}`);
  }
  return response.json();
};

const SOURCE_LABEL: Record<string, string> = {
  api: '공공데이터 자동',
  builtin: '기본 제공',
  organization: '매장 직접 등록',
};

const SelfStudyHolidays: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [holidays, setHolidays] = useState<HolidayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  // window.confirm 은 환경에 따라 차단되어 "반응 없음"으로 보인다 → 인라인 2단계 확인
  const [confirmKey, setConfirmKey] = useState<string | null>(null);

  const load = useCallback(async (targetYear: number) => {
    try {
      setLoading(true);
      const data = await apiCall<HolidayItem[]>(`/api/v1/owner/holidays?year=${targetYear}`);
      setHolidays(data);
    } catch (e) {
      console.error('휴일 조회 실패', e);
      setHolidays([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(year);
  }, [year, load]);

  const handleAdd = async () => {
    if (!newDate) {
      alert('날짜를 선택해주세요.');
      return;
    }
    try {
      await apiCall('/api/v1/owner/holidays', {
        method: 'POST',
        body: JSON.stringify({
          holiday_date: newDate,
          name: newName || '매장 휴일',
          is_holiday: true,
        }),
      });
      setNewDate('');
      setNewName('');
      await load(year);
    } catch (e) {
      alert(`휴일 등록 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  // 전국 공휴일이지만 이 매장은 정상 운영 → is_holiday=false 보정 등록
  const handleExclude = async (item: HolidayItem) => {
    try {
      await apiCall('/api/v1/owner/holidays', {
        method: 'POST',
        body: JSON.stringify({
          holiday_date: item.holiday_date,
          name: item.name,
          is_holiday: false,
        }),
      });
      setConfirmKey(null);
      await load(year);
    } catch (e) {
      alert(`변경 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  // 매장이 직접 등록한 휴일 삭제
  const handleDelete = async (item: HolidayItem) => {
    try {
      await apiCall(`/api/v1/owner/holidays/${item.holiday_date}`, { method: 'DELETE' });
      setConfirmKey(null);
      await load(year);
    } catch (e) {
      alert(`삭제 실패: ${e instanceof Error ? e.message : e}`);
    }
  };

  return (
    <div className="bg-white border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium text-gray-900">휴일(빨간날) 관리</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            여기 등록된 날은 <strong>하루 전체가 자율학습</strong>으로 처리됩니다 (일요일과 동일).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
          <button
            onClick={() => load(year)}
            className="p-1.5 text-gray-500 hover:text-gray-800"
            title="새로고침"
          >
            <ArrowPathIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
      </div>

      {/* 휴일 추가 */}
      <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-gray-50 rounded">
        <input
          type="date"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        />
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="휴일 이름 (예: 창립기념일)"
          className="border rounded px-2 py-1 text-sm flex-1 min-w-[10rem]"
        />
        <button
          onClick={handleAdd}
          className="flex items-center gap-1 px-3 py-1 bg-rose-600 text-white rounded text-sm hover:bg-rose-700"
        >
          <PlusIcon style={{ width: '1rem', height: '1rem' }} />
          휴일 추가
        </button>
      </div>

      {/* 목록 */}
      {loading ? (
        <p className="text-sm text-gray-500 py-4 text-center">불러오는 중…</p>
      ) : holidays.length === 0 ? (
        <p className="text-sm text-gray-500 py-4 text-center">
          {year}년에 등록된 휴일이 없습니다.
        </p>
      ) : (
        <div className="max-h-72 overflow-y-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="text-left text-gray-600">
                <th className="px-3 py-2 font-medium">날짜</th>
                <th className="px-3 py-2 font-medium">이름</th>
                <th className="px-3 py-2 font-medium">구분</th>
                <th className="px-3 py-2 font-medium text-right">관리</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((item) => (
                <tr key={item.holiday_date} className="border-t">
                  <td className="px-3 py-2 text-rose-600 font-medium">{item.holiday_date}</td>
                  <td className="px-3 py-2">{item.name}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">
                    {SOURCE_LABEL[item.source] || item.source}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {confirmKey === item.holiday_date ? (
                      <span className="inline-flex items-center gap-1">
                        <button
                          onClick={() => (item.is_override ? handleDelete(item) : handleExclude(item))}
                          className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                        >
                          확인
                        </button>
                        <button
                          onClick={() => setConfirmKey(null)}
                          className="px-2 py-0.5 text-xs border rounded hover:bg-gray-50"
                        >
                          취소
                        </button>
                      </span>
                    ) : item.is_override ? (
                      <button
                        onClick={() => setConfirmKey(item.holiday_date)}
                        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
                      >
                        <TrashIcon style={{ width: '0.875rem', height: '0.875rem' }} />
                        삭제
                      </button>
                    ) : (
                      <button
                        onClick={() => setConfirmKey(item.holiday_date)}
                        className="text-xs text-gray-500 hover:text-blue-600"
                      >
                        정상 운영일로
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SelfStudyHolidays;
